import React, { useState } from "react";
import { toast } from "react-toastify";
import { kitchenService } from "../services";
import { useTheme } from "../context/ThemeContext";
import arrow_left from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import suitcase from "../assets/images/icons/suitcase.png";
import locationIcon from "../assets/images/location.png";
import star from "../assets/images/icons/star.png";
import car from "../assets/images/icons/car.png";
import photo from "../assets/images/icons/photo.png";
import shield from "../assets/images/icons/shield.png";

type EditOutletInfoModalProps = {
  setIsEditModalOpen: (open: boolean) => void;
  restaurantProfile: any;
};

const EditOutletInfoModal = ({
  setIsEditModalOpen,
  restaurantProfile,
}: EditOutletInfoModalProps) => {
  const { isDarkMode } = useTheme();
  const [expandedSection, setExpandedSection] = useState<string | null>(
    "",
  );
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    businessName: restaurantProfile?.businessName ,
    businessType: restaurantProfile?.businessType || "Cloud Kitchen",
    ownerName: restaurantProfile?.ownerName ,
    email: restaurantProfile?.email ,
    alternateContact: restaurantProfile?.alternateContact ,
    // Location fields
    address: restaurantProfile?.location?.address ,
    landmark: restaurantProfile?.location?.landmark ,
    city: restaurantProfile?.location?.city ,
    state: restaurantProfile?.location?.state ,
    pincode: restaurantProfile?.location?.pincode ,
    // Restaurant Features
    restaurantCategory: restaurantProfile?.restaurantCategory ,
    totalSeating: restaurantProfile?.seatingCapacity ,
    indoorSeating: restaurantProfile?.indoorSeating ,
    outdoorSeating: restaurantProfile?.outdoorSeating ,
    advanceBookingDays: restaurantProfile?.advanceBookingDays ,
    acAvailability: restaurantProfile?.acAvailable,
    cancellationPolicy: restaurantProfile?.cancellationPolicy,
    parkingAvailable: restaurantProfile?.parkingAvailable ,
    valetService: restaurantProfile?.valetService ,
    alcoholServed: restaurantProfile?.alcoholServed ,
    liveMusic: restaurantProfile?.liveMusic,
    acceptsTableBookings: restaurantProfile?.acceptsTableBookings ,
    qrSelfOrderingEnabled: restaurantProfile?.qrSelfOrderingEnabled ,
    // Delivery Configuration
    deliveryAvailable: restaurantProfile?.deliveryAvailable ,
    deliveryRadius: restaurantProfile?.deliveryRadius,
    deliveryType: restaurantProfile?.deliveryType,
    packagingCharges: restaurantProfile?.packagingCharges ,
    minOrderValue: restaurantProfile?.minimumOrderValue ,
    // Legal & Compliance
    panNumber: restaurantProfile?.panNumber ,
    gstNumber: restaurantProfile?.gstNumber ,
    fssaiLicenseNumber: restaurantProfile?.fssaiLicenseNumber ,
  });
  const [kitchenPhotos, setKitchenPhotos] = useState<any[]>(
    restaurantProfile?.kitchenPhotos || [],
  );
  const [menuImages, setMenuImages] = useState<any[]>(
    restaurantProfile?.menuCardImages || [],
  );
  const [foodImages, setFoodImages] = useState<any[]>(
    restaurantProfile?.foodItemImages || [],
  );
  const [fssaiCertificate, setFssaiCertificate] = useState<any>(
    restaurantProfile?.fssaiCertificate || null,
  );
  const [shopActLicense, setShopActLicense] = useState<any>(
    restaurantProfile?.shopActLicense || null,
  );
  console.log("editrestaurantProfile", restaurantProfile);
  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleToggleChange = (name: string) => {
    setFormData({
      ...formData,
      [name]: !formData[name as keyof typeof formData],
    });
  };

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    const files = e.target.files;
    if (files) {
      const newFiles = Array.from(files);
      if (type === "kitchen") {
        setKitchenPhotos([...kitchenPhotos, ...newFiles]);
      } else if (type === "menu") {
        setMenuImages([...menuImages, ...newFiles]);
      } else if (type === "food") {
        setFoodImages([...foodImages, ...newFiles]);
      }
    }
  };

  const handleRemoveImage = (index: number, type: string) => {
    if (type === "kitchen") {
      setKitchenPhotos(kitchenPhotos.filter((_, i) => i !== index));
    } else if (type === "menu") {
      setMenuImages(menuImages.filter((_, i) => i !== index));
    } else if (type === "food") {
      setFoodImages(foodImages.filter((_, i) => i !== index));
    }
  };

  const handleFileUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string,
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === "fssai") {
        setFssaiCertificate(file);
      } else if (type === "shopAct") {
        setShopActLicense(file);
      }
    }
  };

  const handleSaveChanges = async () => {
    // Validation
    if (!formData.businessName.trim()) {
      toast.error("Business Name is required");
      return;
    }
    if (!formData.ownerName.trim()) {
      toast.error("Owner Name is required");
      return;
    }
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      // Prepare data in the format expected by the API
      const dataToSubmit = {
        businessName: formData.businessName,
        businessType: formData.businessType,
        ownerName: formData.ownerName,
        email: formData.email,
        alternateContact: formData.alternateContact,
      };

      // Submit to API
      const response: any = await kitchenService.submitStep1(dataToSubmit);
      toast.success(response.message || "Basic details updated successfully!");

      // Close the accordion after successful save
      setExpandedSection(null);
    } catch (err: any) {
      toast.error(err.message || "Failed to update data. Please try again.");
      console.error("Error updating basic details:", err);
    } finally {
      setLoading(false);
    }
  };

  const accordionSections = [
    {
      id: "basic",
      title: "Basic Information",
      icon: suitcase,
      iconBg: "bg-[#FFF4E6]",
    },
    {
      id: "location",
      title: "Location",
      icon: locationIcon,
      iconBg: "bg-[#FFF4E6]",
    },
    {
      id: "features",
      title: "Restaurant Features",
      icon: star,
      iconBg: "bg-[#FFF4E6]",
    },
    {
      id: "delivery",
      title: "Delivery Configuration",
      icon: car,
      iconBg: "bg-[#FFF4E6]",
    },
    {
      id: "media",
      title: "Photos & Media",
      icon: photo,
      iconBg: "bg-[#FFF4E6]",
    },
    {
      id: "legal",
      title: "Legal & Compliance",
      icon: shield,
      iconBg: "bg-[#FFF4E6]",
    },
  ];

  return (
    <div
      className={`fixed inset-0 z-[70] transition-opacity duration-300 ${
        isDarkMode ? 'bg-black bg-opacity-70' : 'bg-black bg-opacity-50'
      }`}
      onClick={() => setIsEditModalOpen(false)}
    >
      <div
        className={`fixed right-0 top-0 h-full w-full max-w-[450px] transform transition-transform duration-300 ease-in-out rounded-l-[20px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] overflow-y-auto ${
          isDarkMode ? 'bg-[#1A1A1A]' : 'bg-[#FFF8F0]'
        }`}
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`w-full inline-flex flex-col items-start rounded-l-[24px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] p-4 ${
          isDarkMode ? 'bg-[#1A1A1A]' : 'bg-[#FFF8F0]'
        }`}>
          <div className="w-full">
            {/* Modal Header */}
            <div className={`w-full flex justify-between items-center pb-4 border-b ${
              isDarkMode ? 'border-gray-700' : 'border-gray-200'
            }`}>
              <div className="flex items-center gap-3">
                <img
                  src={arrow_left}
                  alt="back"
                  className={`flex w-[36px] h-[36px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] cursor-pointer ${
                    isDarkMode
                      ? 'border-gray-600 bg-gray-800 invert'
                      : 'border-[rgba(112,107,102,0.25)] bg-white'
                  }`}
                  onClick={() => setIsEditModalOpen(false)}
                />
                <h2 className={`font-Urbanist text-[20px] font-semibold leading-[140%] ${
                  isDarkMode ? 'text-white' : 'text-[#050505]'
                }`}>
                  Edit Outlet Info
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <img
                  src={cancel}
                  alt="close"
                  className={`flex w-[36px] h-[36px] p-[7.5px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] cursor-pointer ${
                    isDarkMode
                      ? 'border-gray-600 bg-gray-800 invert'
                      : 'border-[rgba(112,107,102,0.25)] bg-white'
                  }`}
                  onClick={() => setIsEditModalOpen(false)}
                />
              </div>
            </div>

            {/* Accordion Sections */}
            <div className="w-full space-y-3 mt-4">
              {accordionSections.map((section) => (
                <div
                  key={section.id}
                  className={`w-full rounded-[16px] overflow-hidden ${
                    isDarkMode ? 'bg-gray-800' : 'bg-white'
                  }`}
                >
                  {/* Accordion Header */}
                  <div
                    className={`flex items-center justify-between p-4 cursor-pointer transition-colors ${
                      isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => toggleSection(section.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-[48px] h-[48px] ${section.iconBg} rounded-[12px] flex items-center justify-center`}
                      >
                        <img
                          src={section.icon}
                          alt={section.title}
                          className="w-[24px] h-[24px]"
                        />
                      </div>
                      <span className={`font-Urbanist text-[16px] font-semibold leading-[140%] ${
                        isDarkMode ? 'text-white' : 'text-[#050505]'
                      }`}>
                        {section.title}
                      </span>
                    </div>
                    <svg
                      className={`w-6 h-6 transition-transform duration-200 ${
                        expandedSection === section.id ? "rotate-180" : ""
                      } ${isDarkMode ? 'text-gray-300' : 'text-gray-800'}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>

                  {/* Accordion Content */}
                  {expandedSection === section.id && (
                    <div className={`px-4 pb-4 border-t ${
                      isDarkMode ? 'border-gray-700' : 'border-gray-100'
                    }`}>
                      {section.id === "basic" && (
                        <div className="space-y-4 mt-4">
                          {/* Business Name */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Business Name *
                            </label>
                            <input
                              type="text"
                              name="businessName"
                              value={formData.businessName}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter business name"
                            />
                          </div>

                          {/* Kitchen Type */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Business Type *
                            </label>
                            <select
                              name="businessType"
                              value={formData.businessType}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] appearance-none ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              style={{
                                backgroundImage: isDarkMode
                                  ? `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23FFFFFF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23050505' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 1rem center",
                              }}
                            >
                              <option value="CLOUD_KITCHEN">
                                Cloud Kitchen
                              </option>
                              <option value="RESTAURANT">Restaurant</option>
                            </select>
                          </div>

                          {/* Owner Name */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Owner Name *
                            </label>
                            <input
                              type="text"
                              name="ownerName"
                              value={formData.ownerName}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter owner name"
                            />
                          </div>

                          {/* Email */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Email *
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter email"
                            />
                          </div>

                          {/* Alternate Contact */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Alternate Contact
                            </label>
                            <input
                              type="tel"
                              name="alternateContact"
                              value={formData.alternateContact}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter alternate contact"
                            />
                          </div>

                          {/* Save Changes Button */}
                          <button
                            onClick={handleSaveChanges}
                            disabled={loading}
                            className="w-full py-4 bg-[#F3A64E] rounded-[12px] text-white font-Urbanist text-[16px] font-semibold leading-[140%] hover:bg-[#E09239] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      )}

                      {section.id === "location" && (
                        <div className="space-y-4 mt-4">
                          {/* Address */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Address *
                            </label>
                            <textarea
                              name="address"
                              value={formData.address}
                              onChange={(e) => handleInputChange(e as any)}
                              rows={3}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter full address"
                            />
                          </div>

                          {/* Landmark */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Landmark
                            </label>
                            <input
                              type="text"
                              name="landmark"
                              value={formData.landmark}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Near landmark"
                            />
                          </div>

                          {/* City and State */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                City *
                              </label>
                              <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="City"
                              />
                            </div>
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                State *
                              </label>
                              <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="State"
                              />
                            </div>
                          </div>

                          {/* Pincode */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Pincode *
                            </label>
                            <input
                              type="text"
                              name="pincode"
                              value={formData.pincode}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter pincode"
                            />
                          </div>

                          {/* Save Changes Button */}
                          <button
                            onClick={handleSaveChanges}
                            disabled={loading}
                            className="w-full py-4 bg-[#F3A64E] rounded-[12px] text-white font-Urbanist text-[16px] font-semibold leading-[140%] hover:bg-[#E09239] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      )}

                      {section.id === "features" && (
                        <div className="space-y-4 mt-4">
                          {/* Restaurant Category */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Restaurant Category
                            </label>
                            <select
                              name="restaurantCategory"
                              value={formData.restaurantCategory}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] appearance-none ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              style={{
                                backgroundImage: isDarkMode
                                  ? `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23FFFFFF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23050505' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 1rem center",
                              }}
                            >
                              <option value="Fast Food">Fast Food</option>
                              <option value="Fine Dining">Fine Dining</option>
                              <option value="Casual Dining">
                                Casual Dining
                              </option>
                              <option value="Cafe">Cafe</option>
                              <option value="Bakery">Bakery</option>
                            </select>
                          </div>

                          {/* Seating Information */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                Total Seating
                              </label>
                              <input
                                type="number"
                                name="totalSeating"
                                value={formData.totalSeating}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                Indoor Seating
                              </label>
                              <input
                                type="number"
                                name="indoorSeating"
                                value={formData.indoorSeating}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="0"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                Outdoor Seating
                              </label>
                              <input
                                type="number"
                                name="outdoorSeating"
                                value={formData.outdoorSeating}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                Advance Booking Days
                              </label>
                              <input
                                type="number"
                                name="advanceBookingDays"
                                value={formData.advanceBookingDays}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="0"
                              />
                            </div>
                          </div>

                          {/* AC Availability */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              AC Availability
                            </label>
                            <select
                              name="acAvailability"
                              value={formData.acAvailability}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] appearance-none ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              style={{
                                backgroundImage: isDarkMode
                                  ? `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23FFFFFF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23050505' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 1rem center",
                              }}
                            >
                              <option value="Full AC">Full AC</option>
                              <option value="Partial AC">Partial AC</option>
                              <option value="No AC">No AC</option>
                            </select>
                          </div>

                          {/* Cancellation Policy */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Cancellation Policy
                            </label>
                            <select
                              name="cancellationPolicy"
                              value={formData.cancellationPolicy}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] appearance-none ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              style={{
                                backgroundImage: isDarkMode
                                  ? `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23FFFFFF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23050505' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 1rem center",
                              }}
                            >
                              <option value="Free">Free</option>
                              <option value="10% Charge">10% Charge</option>
                              <option value="20% Charge">20% Charge</option>
                              <option value="Non-Refundable">
                                Non-Refundable
                              </option>
                            </select>
                          </div>

                          {/* Feature Toggles */}
                          <div className={`rounded-[12px] border divide-y ${
                            isDarkMode
                              ? 'bg-gray-800 border-gray-700 divide-gray-700'
                              : 'bg-white border-gray-200 divide-gray-200'
                          }`}>
                            {/* Parking Available */}
                            <div className="flex items-center justify-between p-4">
                              <span className={`font-Urbanist text-[16px] font-normal leading-[140%] ${
                                isDarkMode ? 'text-white' : 'text-[#050505]'
                              }`}>
                                Parking Available
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleChange("parkingAvailable")
                                }
                                className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors ${
                                  formData.parkingAvailable
                                    ? "bg-[#F3A64E]"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-[24px] w-[24px] transform rounded-full bg-white transition-transform ${
                                    formData.parkingAvailable
                                      ? "translate-x-[26px]"
                                      : "translate-x-[2px]"
                                  }`}
                                />
                              </button>
                            </div>

                            {/* Valet Service */}
                            <div className="flex items-center justify-between p-4">
                              <span className={`font-Urbanist text-[16px] font-normal leading-[140%] ${
                                isDarkMode ? 'text-white' : 'text-[#050505]'
                              }`}>
                                Valet Service
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleChange("valetService")
                                }
                                className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors ${
                                  formData.valetService
                                    ? "bg-[#F3A64E]"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-[24px] w-[24px] transform rounded-full bg-white transition-transform ${
                                    formData.valetService
                                      ? "translate-x-[26px]"
                                      : "translate-x-[2px]"
                                  }`}
                                />
                              </button>
                            </div>

                            {/* Alcohol Served */}
                            <div className="flex items-center justify-between p-4">
                              <span className={`font-Urbanist text-[16px] font-normal leading-[140%] ${
                                isDarkMode ? 'text-white' : 'text-[#050505]'
                              }`}>
                                Alcohol Served
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  handleToggleChange("alcoholServed")
                                }
                                className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors ${
                                  formData.alcoholServed
                                    ? "bg-[#F3A64E]"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-[24px] w-[24px] transform rounded-full bg-white transition-transform ${
                                    formData.alcoholServed
                                      ? "translate-x-[26px]"
                                      : "translate-x-[2px]"
                                  }`}
                                />
                              </button>
                            </div>

                            {/* Live Music */}
                            <div className="flex items-center justify-between p-4">
                              <span className={`font-Urbanist text-[16px] font-normal leading-[140%] ${
                                isDarkMode ? 'text-white' : 'text-[#050505]'
                              }`}>
                                Live Music
                              </span>
                              <button
                                type="button"
                                onClick={() => handleToggleChange("liveMusic")}
                                className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors ${
                                  formData.liveMusic
                                    ? "bg-[#F3A64E]"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-[24px] w-[24px] transform rounded-full bg-white transition-transform ${
                                    formData.liveMusic
                                      ? "translate-x-[26px]"
                                      : "translate-x-[2px]"
                                  }`}
                                />
                              </button>
                            </div>

                            {/* Accepts Table Booking */}
                            <div className="flex items-center justify-between p-4">
                              <span className={`font-Urbanist text-[16px] font-normal leading-[140%] ${
                                isDarkMode ? 'text-white' : 'text-[#050505]'
                              }`}>
                                Accepts Table Booking
                              </span>
                              <button
                                type="button"
                                onClick={() => handleToggleChange("acceptsTableBookings")}
                                className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors ${
                                  formData.acceptsTableBookings
                                    ? "bg-[#F3A64E]"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-[24px] w-[24px] transform rounded-full bg-white transition-transform ${
                                    formData.acceptsTableBookings
                                      ? "translate-x-[26px]"
                                      : "translate-x-[2px]"
                                  }`}
                                />
                              </button>
                            </div>

                            {/* QR Self-Ordering */}
                            <div className="flex items-center justify-between p-4">
                              <span className={`font-Urbanist text-[16px] font-normal leading-[140%] ${
                                isDarkMode ? 'text-white' : 'text-[#050505]'
                              }`}>
                                QR Self-Ordering
                              </span>
                              <button
                                type="button"
                                onClick={() => handleToggleChange("qrSelfOrderingEnabled")}
                                className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors ${
                                  formData.qrSelfOrderingEnabled
                                    ? "bg-[#F3A64E]"
                                    : "bg-gray-300"
                                }`}
                              >
                                <span
                                  className={`inline-block h-[24px] w-[24px] transform rounded-full bg-white transition-transform ${
                                    formData.qrSelfOrderingEnabled
                                      ? "translate-x-[26px]"
                                      : "translate-x-[2px]"
                                  }`}
                                />
                              </button>
                            </div>
                          </div>

                          {/* Save Changes Button */}
                          <button
                            onClick={handleSaveChanges}
                            disabled={loading}
                            className="w-full py-4 bg-[#F3A64E] rounded-[12px] text-white font-Urbanist text-[16px] font-semibold leading-[140%] hover:bg-[#E09239] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      )}

                      {section.id === "delivery" && (
                        <div className="space-y-4 mt-4">
                          {/* Delivery Available Toggle */}
                          <div className={`flex items-center justify-between p-4 rounded-[12px] border ${
                            isDarkMode
                              ? 'bg-gray-800 border-gray-700'
                              : 'bg-white border-gray-200'
                          }`}>
                            <span className={`font-Urbanist text-[16px] font-normal leading-[140%] ${
                              isDarkMode ? 'text-white' : 'text-[#050505]'
                            }`}>
                              Delivery Available
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                handleToggleChange("deliveryAvailable")
                              }
                              className={`relative inline-flex h-[28px] w-[52px] items-center rounded-full transition-colors ${
                                formData.deliveryAvailable
                                  ? "bg-[#F3A64E]"
                                  : "bg-gray-300"
                              }`}
                            >
                              <span
                                className={`inline-block h-[24px] w-[24px] transform rounded-full bg-white transition-transform ${
                                  formData.deliveryAvailable
                                    ? "translate-x-[26px]"
                                    : "translate-x-[2px]"
                                }`}
                              />
                            </button>
                          </div>

                          {/* Delivery Radius */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Delivery Radius
                            </label>
                            <select
                              name="deliveryRadius"
                              value={formData.deliveryRadius}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] appearance-none ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              style={{
                                backgroundImage: isDarkMode
                                  ? `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23FFFFFF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23050505' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 1rem center",
                              }}
                            >
                              <option value="2 km">2 km</option>
                              <option value="5 km">5 km</option>
                              <option value="10 km">10 km</option>
                              <option value="15 km">15 km</option>
                              <option value="20 km">20 km</option>
                            </select>
                          </div>

                          {/* Delivery Type */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Delivery Type
                            </label>
                            <select
                              name="deliveryType"
                              value={formData.deliveryType}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] appearance-none ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              style={{
                                backgroundImage: isDarkMode
                                  ? `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23FFFFFF' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`
                                  : `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23050505' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 1rem center",
                              }}
                            >
                              <option value="Self Delivery">
                                Self Delivery
                              </option>
                              <option value="Third Party">Third Party</option>
                              <option value="Both">Both</option>
                            </select>
                          </div>

                          {/* Packaging Charges and Min Order Value */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                Packaging Charges (₹)
                              </label>
                              <input
                                type="number"
                                name="packagingCharges"
                                value={formData.packagingCharges}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                                isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                              }`}>
                                Min. Order Value (₹)
                              </label>
                              <input
                                type="number"
                                name="minOrderValue"
                                value={formData.minOrderValue}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                  isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                    : 'bg-white border-gray-200 text-[#050505]'
                                }`}
                                placeholder="0"
                              />
                            </div>
                          </div>

                          {/* Save Changes Button */}
                          <button
                            onClick={handleSaveChanges}
                            disabled={loading}
                            className="w-full py-4 bg-[#F3A64E] rounded-[12px] text-white font-Urbanist text-[16px] font-semibold leading-[140%] hover:bg-[#E09239] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      )}

                      {section.id === "media" && (
                        <div className="space-y-4 mt-4">
                          {/* Kitchen Photos */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Kitchen Photos
                            </label>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              {kitchenPhotos.map((photo, index) => (
                                <div key={index} className="relative">
                                  <img
                                    src={
                                      typeof photo === "string"
                                        ? photo
                                        : URL.createObjectURL(photo)
                                    }
                                    alt={`Kitchen ${index + 1}`}
                                    className="w-full h-[120px] object-cover rounded-[12px]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveImage(index, "kitchen")
                                    }
                                    className="absolute top-2 right-2 w-[24px] h-[24px] bg-red-500 rounded-full flex items-center justify-center text-white"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                            <label className={`w-full py-4 border-2 border-dashed rounded-[12px] flex items-center justify-center gap-2 cursor-pointer transition-colors ${
                              isDarkMode
                                ? 'border-[#F3A64E] hover:bg-gray-700'
                                : 'border-[#F3A64E] hover:bg-[#FFF8F0]'
                            }`}>
                              <span className="text-[#F3A64E] text-[24px]">
                                +
                              </span>
                              <span className="text-[#F3A64E] font-Urbanist text-[16px] font-semibold leading-[140%]">
                                Add Kitchen Photos
                              </span>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={(e) =>
                                  handleImageUpload(e, "kitchen")
                                }
                                className="hidden"
                              />
                            </label>
                          </div>

                          {/* Menu Card Images */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Menu Card Images
                            </label>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              {menuImages.map((photo, index) => (
                                <div key={index} className="relative">
                                  <img
                                    src={
                                      typeof photo === "string"
                                        ? photo
                                        : URL.createObjectURL(photo)
                                    }
                                    alt={`Menu ${index + 1}`}
                                    className="w-full h-[120px] object-cover rounded-[12px]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveImage(index, "menu")
                                    }
                                    className="absolute top-2 right-2 w-[24px] h-[24px] bg-red-500 rounded-full flex items-center justify-center text-white"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                            <label className={`w-full py-4 border-2 border-dashed rounded-[12px] flex items-center justify-center gap-2 cursor-pointer transition-colors ${
                              isDarkMode
                                ? 'border-[#F3A64E] hover:bg-gray-700'
                                : 'border-[#F3A64E] hover:bg-[#FFF8F0]'
                            }`}>
                              <span className="text-[#F3A64E] text-[24px]">
                                +
                              </span>
                              <span className="text-[#F3A64E] font-Urbanist text-[16px] font-semibold leading-[140%]">
                                Add Menu Images
                              </span>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, "menu")}
                                className="hidden"
                              />
                            </label>
                          </div>

                          {/* Food Item Images */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Food Item Images
                            </label>
                            <div className="grid grid-cols-2 gap-3 mb-3">
                              {foodImages.map((photo, index) => (
                                <div key={index} className="relative">
                                  <img
                                    src={
                                      typeof photo === "string"
                                        ? photo
                                        : URL.createObjectURL(photo)
                                    }
                                    alt={`Food ${index + 1}`}
                                    className="w-full h-[120px] object-cover rounded-[12px]"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleRemoveImage(index, "food")
                                    }
                                    className="absolute top-2 right-2 w-[24px] h-[24px] bg-red-500 rounded-full flex items-center justify-center text-white"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                            <label className={`w-full py-4 border-2 border-dashed rounded-[12px] flex items-center justify-center gap-2 cursor-pointer transition-colors ${
                              isDarkMode
                                ? 'border-[#F3A64E] hover:bg-gray-700'
                                : 'border-[#F3A64E] hover:bg-[#FFF8F0]'
                            }`}>
                              <span className="text-[#F3A64E] text-[24px]">
                                +
                              </span>
                              <span className="text-[#F3A64E] font-Urbanist text-[16px] font-semibold leading-[140%]">
                                Add Food Images
                              </span>
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, "food")}
                                className="hidden"
                              />
                            </label>
                          </div>

                          {/* Save Changes Button */}
                          <button
                            onClick={handleSaveChanges}
                            disabled={loading}
                            className="w-full py-4 bg-[#F3A64E] rounded-[12px] text-white font-Urbanist text-[16px] font-semibold leading-[140%] hover:bg-[#E09239] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      )}

                      {section.id === "legal" && (
                        <div className="space-y-4 mt-4">
                          {/* PAN Number */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              PAN Number *
                            </label>
                            <input
                              type="text"
                              name="panNumber"
                              value={formData.panNumber}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter PAN number"
                            />
                          </div>

                          {/* GST Number */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              GST Number *
                            </label>
                            <input
                              type="text"
                              name="gstNumber"
                              value={formData.gstNumber}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter GST number"
                            />
                          </div>

                          {/* FSSAI License Number */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              FSSAI License Number *
                            </label>
                            <input
                              type="text"
                              name="fssaiLicenseNumber"
                              value={formData.fssaiLicenseNumber}
                              onChange={handleInputChange}
                              className={`w-full px-4 py-3 rounded-[12px] border font-Urbanist text-[16px] font-normal leading-[140%] focus:outline-none focus:border-[#F3A64E] ${
                                isDarkMode
                                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                  : 'bg-white border-gray-200 text-[#050505]'
                              }`}
                              placeholder="Enter FSSAI license number"
                            />
                          </div>

                          {/* FSSAI Certificate */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              FSSAI Certificate
                            </label>
                            {fssaiCertificate && (
                              <div className={`mb-2 text-sm ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {fssaiCertificate.name ||
                                  "Certificate uploaded"}
                              </div>
                            )}
                            <label className={`w-full py-4 border-2 border-dashed rounded-[12px] flex items-center justify-center gap-2 cursor-pointer transition-colors ${
                              isDarkMode
                                ? 'border-[#F3A64E] hover:bg-gray-700'
                                : 'border-[#F3A64E] hover:bg-[#FFF8F0]'
                            }`}>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10 4.16667V15.8333M4.16669 10H15.8334"
                                  stroke="#F3A64E"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                              <span className="text-[#F3A64E] font-Urbanist text-[16px] font-semibold leading-[140%]">
                                Upload Certificate
                              </span>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleFileUpload(e, "fssai")}
                                className="hidden"
                              />
                            </label>
                          </div>

                          {/* Shop Act License */}
                          <div>
                            <label className={`block font-Urbanist text-[14px] font-normal leading-[140%] mb-2 ${
                              isDarkMode ? 'text-gray-400' : 'text-[#6A6A6A]'
                            }`}>
                              Shop Act License
                            </label>
                            {shopActLicense && (
                              <div className={`mb-2 text-sm ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {shopActLicense.name || "License uploaded"}
                              </div>
                            )}
                            <label className={`w-full py-4 border-2 border-dashed rounded-[12px] flex items-center justify-center gap-2 cursor-pointer transition-colors ${
                              isDarkMode
                                ? 'border-[#F3A64E] hover:bg-gray-700'
                                : 'border-[#F3A64E] hover:bg-[#FFF8F0]'
                            }`}>
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M10 4.16667V15.8333M4.16669 10H15.8334"
                                  stroke="#F3A64E"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                              <span className="text-[#F3A64E] font-Urbanist text-[16px] font-semibold leading-[140%]">
                                Upload License
                              </span>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleFileUpload(e, "shopAct")}
                                className="hidden"
                              />
                            </label>
                          </div>

                          {/* Save Changes Button */}
                          <button
                            onClick={handleSaveChanges}
                            disabled={loading}
                            className="w-full py-4 bg-[#F3A64E] rounded-[12px] text-white font-Urbanist text-[16px] font-semibold leading-[140%] hover:bg-[#E09239] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {loading ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditOutletInfoModal;
